import { Form } from "antd";
import { InputWithLabelAndSvg } from "components/Global";
import { FORMDATA } from "constants/Data";
import { phoneNumberFormatter } from "helper/phoneNumberFormatter";
import React, { FC, useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { invalidText, toastText } from "utils/utils";
import styles from "./index.module.scss";
import { SideDrawerBodyProps } from "./types";
import { postApi, putApi } from "redux/apis";
import {
  parsePhoneNumber,
  isValidPhoneNumber,
  isPossiblePhoneNumber,
} from "libphonenumber-js";

const AddServiceCenterBody: FC<SideDrawerBodyProps> = (props) => {
  // Inits
  const { addServiceCenterFields } = FORMDATA;
  const {
    closeDrawerByAnimation,
    seletcedServiceCenter,
    fetchServiceCenter,
    isViewMode,
  } = props;
  const [form] = Form.useForm();

  // State Management
  const [phoneNumber, setPhoneNumber] = useState(
    seletcedServiceCenter ? seletcedServiceCenter.contactNumber : ""
  );
  const [phoneError, setPhoneError] = useState("");
  const [isAddUserLoading, setIsAddUserLoading] = useState(false);

  const editData = {
    ...seletcedServiceCenter,
    fullName: seletcedServiceCenter?.name || "",
    contactNumber: seletcedServiceCenter?.contactNumber,
  };

  useEffect(() => {
    setValidateDisplayNum(
      seletcedServiceCenter ? seletcedServiceCenter.contactNumber : ""
    );
    // If editing an existing service center with a contact number, mark as interacted
    if (seletcedServiceCenter && seletcedServiceCenter.contactNumber) {
      setHasUserInteracted(true);
    }
  }, [seletcedServiceCenter]);

  const [showError, setShowError] = useState<boolean>();
  const [displayNum, setDisplayNum] = useState(
    seletcedServiceCenter ? seletcedServiceCenter.contactNumber : ""
  );
  const [validateDisplayNum, setValidateDisplayNum] = useState("");
  const [hasUserInteracted, setHasUserInteracted] = useState(false); // Track user interaction
  const [previousCountry, setPreviousCountry] = useState<string>("us"); // Track country changes

  // Enhanced phone number validation using libphonenumber-js
  const validatePhoneNumber = (phoneNumber: string, country: any) => {
    if (!phoneNumber || phoneNumber.trim() === "") {
      return { isValid: false, error: "Contact number is required" };
    }

    try {
      const countryCode = (
        country?.countryCode ||
        country?.iso2 ||
        ""
      ).toUpperCase();

      // Use libphonenumber-js for dynamic validation - no need for manual country rules

      // Parse the phone number with country context
      const parsedNumber = parsePhoneNumber(phoneNumber, countryCode as any);

      if (!parsedNumber) {
        return { isValid: false, error: "Invalid contact number" };
      }

      // Check if the number is valid for the specific country
      if (!parsedNumber.isValid()) {
        return {
          isValid: false,
          error: "Invalid contact number",
        };
      }

      // Additional validation: Check total digit count including country code
      const dialCode = country?.dialCode || "";
      const digitsOnly = phoneNumber.replace(/\D/g, "");

      // Calculate expected total digits (country code + national number)
      const expectedTotalDigits =
        dialCode.length + parsedNumber.nationalNumber.length;

      // Validate total digit count
      if (digitsOnly.length !== expectedTotalDigits) {
        return {
          isValid: false,
          error: "Invalid contact number",
        };
      }

      // Ensure the number starts with the correct country code
      if (!digitsOnly.startsWith(dialCode)) {
        return {
          isValid: false,
          error: "Invalid contact number",
        };
      }

      return { isValid: true, error: "" };
    } catch (error) {
      // Fallback validation if libphonenumber-js fails
      try {
        // Try global validation without country
        if (isValidPhoneNumber(phoneNumber)) {
          return { isValid: true, error: "" };
        } else {
          return { isValid: false, error: "Invalid contact number" };
        }
      } catch (fallbackError) {
        return { isValid: false, error: "Invalid contact number" };
      }
    }
  };

  const phoneChangeHandler = (value: string, country: any) => {
    const formattedValue = phoneNumberFormatter(value, country?.dialCode);
    setDisplayNum(formattedValue?.formattedPhoneNumber);
    setValidateDisplayNum(formattedValue?.cleanedPhoneNumber);

    // Check if country changed
    const currentCountryCode = country?.countryCode || country?.iso2;
    const isCountryChange =
      previousCountry && previousCountry !== currentCountryCode;

    // Mark that user has interacted with the phone input
    setHasUserInteracted(true);

    // Enhanced validation using libphonenumber-js
    const validation = validatePhoneNumber(value, country);

    if (!validation.isValid) {
      setPhoneError(validation.error);
      setShowError(true);
    } else {
      setPhoneError("");
      setShowError(false);
    }

    // Focus management: Focus the input after phone number change or country change
    const focusDelay = isCountryChange ? 200 : 50; // Longer delay for country changes
    setTimeout(() => {
      const phoneInputElement = document.querySelector(
        ".react-tel-input input"
      ) as HTMLInputElement;
      if (phoneInputElement) {
        phoneInputElement.focus();
        // Position cursor at the end of the input
        const length = phoneInputElement.value.length;
        phoneInputElement.setSelectionRange(length, length);
      }
    }, focusDelay);

    // Update previous country
    setPreviousCountry(currentCountryCode);
  };

  // Enhanced form validation function
  const validateForm = () => {
    let isValid = true;

    // Mark that user has interacted when form is submitted
    setHasUserInteracted(true);

    // Validate contact number
    if (invalidText(validateDisplayNum)) {
      setPhoneError("Contact number is required");
      setShowError(true);
      isValid = false;
    } else {
      // Get current country info for validation
      const phoneInputElement = document.querySelector(
        ".react-tel-input .selected-flag"
      ) as HTMLElement;
      const countryCode =
        phoneInputElement
          ?.getAttribute("title")
          ?.split(":")[0]
          ?.toLowerCase() || "us";

      // Create a mock country object for validation
      const mockCountry = {
        countryCode: countryCode,
        iso2: countryCode,
        dialCode: displayNum.match(/^\+(\d+)/)?.[1] || "1",
      };

      const validation = validatePhoneNumber(displayNum, mockCountry);
      if (!validation.isValid) {
        setPhoneError(validation.error);
        setShowError(true);
        isValid = false;
      } else {
        setPhoneError("");
        setShowError(false);
      }
    }

    // Log validation result for debugging
    if (!isValid) {
      console.log("Form validation failed:", {
        phoneError: phoneError,
        hasContactNumber: !invalidText(validateDisplayNum),
      });
    }

    return isValid;
  };

  const onFinish = async (values: any) => {
    // First validate the form completely before proceeding
    const isFormValid = validateForm();
    if (!isFormValid) {
      return; // Stop execution if validation fails
    }

    // Trim all string values before submitting
    const trimmedValues = { ...values };
    Object.keys(trimmedValues).forEach((key) => {
      if (typeof trimmedValues[key] === "string") {
        trimmedValues[key] = trimmedValues[key]?.trim();
      }
    });

    trimmedValues.contactNumber = displayNum;
    if (seletcedServiceCenter) {
      const data: any = {
        id: seletcedServiceCenter?.id,
        ...trimmedValues,
      };

      await updateServiceCenter(data);
    } else {
      await addServiceCenter(trimmedValues);
    }
  };

  const updateServiceCenter = async (serviceCenterData: any) => {
    try {
      setIsAddUserLoading(true);
      const response = await putApi("/serviceCenter/update", serviceCenterData);
      const message =
        response?.data?.message || "Service center updated successfully";
      closeDrawerByAnimation();
      toastText(message, "success");
      fetchServiceCenter();
    } catch (err: any) {
      let message = "Something went wrong in updating branch code";
      if (err.response?.data?.message) {
        message = err.response.data.message;
      }
      toastText(message, "error");
    } finally {
      setIsAddUserLoading(false);
    }
  };

  const addServiceCenter = async (finalData: any) => {
    try {
      setIsAddUserLoading(true);

      const response = await postApi("/serviceCenter/create", finalData);
      const message =
        response?.data?.message || "Service Center created successfully";

      setDisplayNum("");
      closeDrawerByAnimation();
      toastText(message, "success");
      fetchServiceCenter();
    } catch (err: any) {
      let message = "Something went wrong when creating to branch code";
      if (err.response?.data?.message) {
        message = err.response.data.message;
      }
      toastText(message, "error");
    } finally {
      setIsAddUserLoading(false);
    }
  };

  // If form fails
  const onFinishFailed = () => {
    validateForm();
  };

  // JSX
  return (
    <div className={styles["side-drawer-body"]}>
      <Form
        form={form}
        name="basic"
        initialValues={editData}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
        layout="vertical"
        labelAlign="left"
        className={styles["side-drawer-form"]}
        onValuesChange={(changedValues, allValues) => {
          const trimmedValues = Object.fromEntries(
            Object.entries(allValues).map(([key, value]) => [
              key,
              typeof value === "string" ? value.trim() : value,
            ])
          );
          form.setFieldsValue(trimmedValues);
        }}
        // onValuesChange={(changedValues, allValues) => {
        //   const trimmedValues = Object.fromEntries(
        //     Object.entries(allValues).map(([key, value]) => [
        //       key,
        //       typeof value === "string" ? value.trimStart() : value,
        //     ])
        //   );
        //   form.setFieldsValue(trimmedValues);
        // }}
      >
        <div className={styles["side-drawer-form__inputs"]}>
          {addServiceCenterFields.map(
            (singleField: any, index: React.Key | null | undefined) => {
              return (
                <React.Fragment key={index}>
                  {singleField.id === "contactNumber" && (
                    <Form.Item name={singleField.name}>
                      <label
                        className={styles["side-drawer-form__role--label"]}
                      >
                        {singleField.title}{" "}
                        {singleField?.required && (
                          <span
                            className="required-color"
                            style={{ marginLeft: "5px" }}
                          >
                            *
                          </span>
                        )}
                      </label>
                      <PhoneInput
                        country={"us"}
                        value={displayNum}
                        disabled={isViewMode}
                        onChange={phoneChangeHandler}
                        isValid={(value, country) => {
                          // Always return true to prevent react-phone-input-2 from applying its own error styles
                          // We handle error display manually
                          return true;
                        }}
                        enableSearch={true}
                        disableSearchIcon={false}
                        countryCodeEditable={false}
                        containerClass={`${
                          isViewMode
                            ? styles.redBorderDisabled
                            : hasUserInteracted && phoneError
                            ? "has-error"
                            : ""
                        }`}
                        inputClass={`${styles["input-icon__form--input"]} ${
                          styles.customPhoneInput
                        } ${
                          hasUserInteracted && phoneError ? styles.error : ""
                        }`}
                      />

                      {hasUserInteracted && phoneError && (
                        <p
                          className="ant-form-item-explain-error"
                          style={{ paddingTop: "8px", paddingBottom: "8px" }}
                        >
                          {phoneError}
                        </p>
                      )}
                      {hasUserInteracted &&
                        !phoneError &&
                        validateDisplayNum === "" &&
                        showError && (
                          <p
                            className="ant-form-item-explain-error"
                            style={{ paddingTop: "8px", paddingBottom: "8px" }}
                          >
                            Please enter contact number
                          </p>
                        )}
                    </Form.Item>
                  )}
                  {singleField.id !== "contactNumber" && (
                    <InputWithLabelAndSvg
                      singleUserInput={singleField}
                      disabled={isViewMode}
                      setPhoneNumber={setPhoneNumber}
                      phoneNumber={phoneNumber}
                      phoneError={phoneError}
                      setPhoneError={setPhoneError}
                    />
                  )}
                </React.Fragment>
              );
            }
          )}
        </div>
        {!isViewMode && (
          <div className={styles["side-drawer-form__buttons"]}>
            <Form.Item>
              <button
                type="submit"
                className={`${
                  styles[
                    seletcedServiceCenter
                      ? "side-drawer-form__buttons--edit"
                      : "side-drawer-form__buttons--save"
                  ]
                } ${isAddUserLoading && "pointer-event-none"}`}
              >
                {isAddUserLoading ? (
                  <img
                    src={`${process.env.REACT_APP_IMAGES_BASE_URL}/assets/gifs/loading-black.gif`}
                    crossOrigin={
                      process.env.REACT_APP_ENV === "local"
                        ? undefined
                        : "anonymous"
                    }
                    height={40}
                  />
                ) : (
                  "Save"
                )}
              </button>
            </Form.Item>
            <Form.Item>
              <button
                type="reset"
                className={`${styles["side-drawer-form__buttons--cancel"]}`}
                onClick={closeDrawerByAnimation}
                disabled={isAddUserLoading}
              >
                Cancel
              </button>
            </Form.Item>
          </div>
        )}
      </Form>
    </div>
  );
};

export default AddServiceCenterBody;
